import CancelIcon from '@mui/icons-material/Cancel';
import SaveIcon from '@mui/icons-material/Save';
import React, { useState } from 'react';

import { AiVoice } from '_lib/api/apiTypes.shim';
import { Button } from '_lib/components/Button/Button';
import { Divider } from '_lib/components/Divider/Divider';
import { FlexBox } from '_lib/components/FlexBox/FlexBox';
import { FlexCard } from '_lib/components/FlexCard/FlexCard';
import { MenuItem } from '_lib/components/MenuItem/MenuItem';
import { Stack } from '_lib/components/Stack/Stack';
import { TextField } from '_lib/components/TextField/TextField';

import { VoiceSelectionItem } from './VoiceSelectionItem';

type VoiceEditCardProps = {
  initialPrimaryId?: string | null;
  initialFallbackId?: string | null;
  aiVoices: AiVoice[];
  isActive?: boolean;
  onCancel: () => void;
  onSave: (voice: { primaryVoiceId: string; fallbackVoiceId: string }) => void;
};

export const VoiceEditCard = (props: VoiceEditCardProps) => {
  const { initialPrimaryId, initialFallbackId, aiVoices, isActive = true, onCancel, onSave } = props;

  const [primaryId, setPrimaryId] = useState<string | null>(initialPrimaryId || null);
  const [fallbackId, setFallbackId] = useState<string | null>(initialFallbackId || null);

  const getAvailableFallbackVoices = () => {
    if (!primaryId || !aiVoices) return [];
    const primary = aiVoices.find((v) => v.id === primaryId);
    return primary ? aiVoices.filter((v) => v.providerVoice.provider !== primary.providerVoice.provider) : [];
  };

  return (
    <FlexCard>
      <Stack useFlexGap sx={{ padding: 2, width: '100%' }} spacing={2}>
        <TextField
          select
          label='Primary Voice'
          value={primaryId || ''}
          onChange={(e) => {
            setPrimaryId(e.target.value);
            setFallbackId(null);
          }}
          required
          disabled={!isActive}
          fullWidth>
          {aiVoices.map((voice) => (
            <MenuItem key={voice.id} value={voice.id}>
              <VoiceSelectionItem voice={voice} />
            </MenuItem>
          ))}
        </TextField>

        <TextField
          select
          label='Fallback Voice'
          value={fallbackId || ''}
          onChange={(e) => setFallbackId(e.target.value)}
          disabled={!primaryId || !isActive}
          required
          helperText='NOTE: Fallback voice must be from a different provider'
          fullWidth>
          {getAvailableFallbackVoices().map((voice) => (
            <MenuItem key={voice.id} value={voice.id}>
              <VoiceSelectionItem voice={voice} />
            </MenuItem>
          ))}
        </TextField>
      </Stack>
      <Divider />
      <FlexBox sx={{ justifyContent: 'flex-end', gap: 1, marginY: 1, marginRight: 1 }}>
        <Button
          text='Cancel'
          variant='contained'
          color='error'
          size='small'
          startIcon={<CancelIcon />}
          onClick={onCancel}
        />
        <Button
          text='Save'
          variant='contained'
          size='small'
          startIcon={<SaveIcon />}
          onClick={() => primaryId && fallbackId && onSave({ primaryVoiceId: primaryId, fallbackVoiceId: fallbackId })}
          disabled={!primaryId || !fallbackId || !isActive}
        />
      </FlexBox>
    </FlexCard>
  );
};
